import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import PropertyAutocomplete from "@/components/PropertyAutocomplete";
import { Calculator, Check } from "lucide-react";
import { toast } from "sonner";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

// Stable, module-scope money input. It keeps a RAW editing string in local state and
// only formats with commas when the field is blurred — so typing never re-renders a new
// input node and focus/cursor never resets. Defining it outside the page component is
// what actually fixes the "loses focus after one digit" bug (inline components remount
// on every keystroke).
function MoneyField({ label, value, onChange, prefix = "$", min = 0, testid }) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState("");
  const display = focused ? text : (value ? fmt(value) : "");
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>
        <Input
          type="text"
          inputMode="numeric"
          data-testid={testid}
          value={display}
          onFocus={(e) => {
            setText(value ? String(value) : "");
            setFocused(true);
            requestAnimationFrame(() => { try { e.target.select(); } catch { /* noop */ } });
          }}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9]/g, "");
            setText(raw);
            onChange(clamp(Number(raw) || 0, min, 1e9));
          }}
          className="pl-6 font-mono"
        />
      </div>
    </div>
  );
}

function PercentSlider({ label, value, onChange, min, max, step = 1, suffix = "%", testid }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><Label>{label}</Label><span className="font-mono font-semibold">{value}{suffix}</span></div>
      <Slider data-testid={testid} min={min} max={max} step={step} value={[value]} onValueChange={([v]) => onChange(v)} />
    </div>
  );
}

const Row = ({ k, v, bold = false, color = "" }) => (
  <div className="flex justify-between text-xs py-1 border-b border-slate-100 last:border-b-0">
    <span className="text-slate-600">{k}</span>
    <span className={`font-mono ${bold ? "font-bold" : ""} ${color || "text-slate-900"}`}>{v}</span>
  </div>
);
const M = ({ k, v }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
    <div className="font-mono text-sm font-bold">{v}</div>
  </div>
);

export default function Underwriting() {
  const [sp] = useSearchParams();
  const [prop, setProp] = useState(null);
  const [result, setResult] = useState(null);
  const [savedAt, setSavedAt] = useState(0);
  const skipSaveRef = useRef(true);
  const [inp, setInp] = useState({
    acquisition_cost: 0,
    conversion_cost: 0,
    monthly_specialized_rent: 0,
    monthly_conventional_rent: 0,
    down_payment_pct: 25,
    interest_rate: 7.5,
    term_years: 30,
    spec_vacancy: 5,
    spec_opex_pct: 35,
    conv_vacancy: 8,
    conv_opex_pct: 40,
  });

  const setK = (k, v) => setInp((i) => ({ ...i, [k]: v }));

  // Load property from URL param or pick first
  useEffect(() => {
    const pid = sp.get("property");
    if (pid) {
      api.get(`/properties/${pid}`).then(r => setProp(r.data));
    } else {
      api.get("/properties").then(r => { if (r.data[0]) setProp(r.data[0]); });
    }
  }, [sp]);

  // When property changes, pull its numbers into the inputs (as editable defaults)
  useEffect(() => {
    if (prop) {
      skipSaveRef.current = true; // don't auto-save the values we just loaded
      setInp(i => ({
        ...i,
        ...(prop.uw_inputs || {}),
        acquisition_cost: prop.acquisition_cost || 0,
        conversion_cost: prop.conversion_cost || 0,
        monthly_specialized_rent: prop.monthly_specialized_rent || 0,
        monthly_conventional_rent: prop.monthly_conventional_rent || 0,
      }));
    }
  }, [prop?.id]);

  const run = async () => {
    if (!prop) return;
    const { data } = await api.post("/underwriting/run", { property_id: prop.id, inputs: inp });
    setResult(data);
  };

  // Auto-run when property loads or when inputs change (debounced)
  useEffect(() => {
    if (!prop) return;
    const t = setTimeout(run, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop?.id, inp]);

  // Auto-persist inputs to the property so they flow into any packet built from it.
  useEffect(() => {
    if (!prop) return;
    if (skipSaveRef.current) { skipSaveRef.current = false; return; }
    const t = setTimeout(() => saveToProperty(true), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inp]);

  const saveToProperty = async (silent = false) => {
    if (!prop) return;
    try {
      await api.post(`/properties/${prop.id}/uw-inputs`, {
        acquisition_cost: inp.acquisition_cost,
        conversion_cost: inp.conversion_cost,
        monthly_conventional_rent: inp.monthly_conventional_rent,
        monthly_specialized_rent: inp.monthly_specialized_rent,
        uw_inputs: inp,
      });
      setSavedAt(Date.now());
      if (!silent) toast.success("Underwriting saved to property — flows into the packet");
    } catch {
      if (!silent) toast.error("Could not save underwriting inputs");
    }
  };

  return (
    <div className="space-y-6" data-testid="underwriting-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2"><Calculator className="w-3.5 h-3.5"/>Scenario Simulator</div>
        <h1 className="text-3xl font-bold">Underwriting</h1>
        <p className="text-xs text-slate-500 mt-1">Specialized housing vs conventional rental — all inputs are editable and save to the property automatically. Projections only, not investment advice. Need more scenarios? <a href="/scenarios" className="text-[#C25E00] hover:underline" data-testid="scenarios-link">Compare 5+ use scenarios →</a></p>
      </div>

      <Card className="p-5 border-slate-200">
        <Label>Property</Label>
        <PropertyAutocomplete
          testid="uw-property-search"
          placeholder="Search a property by address…"
          onSelect={(p) => setProp(p)}
        />
        {prop && (
          <div className="text-xs text-slate-600 mt-2">
            Loaded: <span className="font-semibold">{prop.address}</span> · {prop.city}, TX {prop.zip_code} · {prop.county} County · {prop.bedrooms}BR/{prop.bathrooms}BA
          </div>
        )}
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <h3 className="text-sm uppercase tracking-widest text-slate-500 font-semibold">Deal Inputs</h3>
            <div className="grid grid-cols-2 gap-3">
              <MoneyField label="Purchase Price" value={inp.acquisition_cost} onChange={(v) => setK("acquisition_cost", v)} testid="uw-purchase-price" />
              <MoneyField label="Conversion Cost" value={inp.conversion_cost} onChange={(v) => setK("conversion_cost", v)} testid="uw-conversion-cost" />
              <MoneyField label="Specialized Rent (mo)" value={inp.monthly_specialized_rent} onChange={(v) => setK("monthly_specialized_rent", v)} testid="uw-spec-rent" />
              <MoneyField label="Conventional Rent (mo)" value={inp.monthly_conventional_rent} onChange={(v) => setK("monthly_conventional_rent", v)} testid="uw-conv-rent" />
            </div>
            <PercentSlider label="Down Payment" value={inp.down_payment_pct} onChange={(v) => setK("down_payment_pct", v)} min={0} max={100} testid="uw-down-slider" />
            <PercentSlider label="Interest Rate" value={inp.interest_rate} onChange={(v) => setK("interest_rate", v)} min={3} max={12} step={0.25} testid="uw-rate-slider" />
            <div>
              <div className="flex justify-between text-xs mb-1"><Label>Loan Term (years)</Label><span className="font-mono font-semibold">{inp.term_years}</span></div>
              <Slider min={5} max={30} step={5} value={[inp.term_years]} onValueChange={([v]) => setK("term_years", v)} />
            </div>
            <div className="pt-2 border-t border-slate-100"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Specialized Scenario</div>
              <div className="space-y-3">
                <PercentSlider label="Vacancy" value={inp.spec_vacancy} onChange={(v) => setK("spec_vacancy", v)} min={0} max={30} testid="uw-spec-vac"/>
                <PercentSlider label="OpEx %" value={inp.spec_opex_pct} onChange={(v) => setK("spec_opex_pct", v)} min={20} max={60} testid="uw-spec-opex"/>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Conventional Scenario</div>
              <div className="space-y-3">
                <PercentSlider label="Vacancy" value={inp.conv_vacancy} onChange={(v) => setK("conv_vacancy", v)} min={0} max={30} testid="uw-conv-vac"/>
                <PercentSlider label="OpEx %" value={inp.conv_opex_pct} onChange={(v) => setK("conv_opex_pct", v)} min={20} max={60} testid="uw-conv-opex"/>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={run} className="bg-slate-900 hover:bg-slate-800 flex-1" data-testid="uw-run">Recalculate now</Button>
              <Button onClick={() => saveToProperty(false)} variant="outline" data-testid="uw-save">
                {savedAt ? <><Check className="w-4 h-4 mr-1 text-emerald-600"/>Saved</> : "Save to property"}
              </Button>
            </div>
          </div>

          <div>
            {result ? (
              <div className="grid grid-cols-1 gap-3">
                {Object.entries(result.scenarios).map(([label, s]) => (
                  <Card key={label} className={`p-4 border-slate-200 ${label === "specialized" ? "bg-slate-50" : ""}`} data-testid={`uw-result-${label}`}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3 capitalize">{label}</div>
                    <Row k="Gross Monthly Rent" v={`$${Number(s.gross_monthly_rent).toLocaleString()}`} />
                    <Row k="EGI (annual)" v={`$${Math.round(s.effective_gross_income).toLocaleString()}`} />
                    <Row k="Operating Exp" v={`$${Math.round(s.operating_expenses).toLocaleString()}`} />
                    <Row k="NOI" v={`$${Math.round(s.noi).toLocaleString()}`} bold />
                    <Row k="Annual Debt Service" v={`$${Math.round(s.annual_debt_service).toLocaleString()}`} />
                    <Row k="Annual Cash Flow" v={`$${Math.round(s.annual_cash_flow).toLocaleString()}`} bold color={label === "specialized" ? "text-[#C25E00]" : ""} />
                    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-200">
                      <M k="Cash-on-Cash" v={`${s.cash_on_cash_pct}%`} />
                      <M k="Cap Rate" v={`${s.cap_rate_pct}%`} />
                      <M k="DSCR" v={`${s.dscr}`} />
                    </div>
                  </Card>
                ))}
                {result.scenarios?.specialized && result.scenarios?.conventional && (
                  <Card className="p-4 border-slate-200 bg-[#C25E00]/5">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Specialized Uplift</div>
                    <div className="text-2xl font-bold text-[#C25E00] font-mono">
                      +${Math.round(result.scenarios.specialized.annual_cash_flow - result.scenarios.conventional.annual_cash_flow).toLocaleString()}/yr
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">Cash flow difference vs conventional</div>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-500 p-8 text-center border border-dashed border-slate-200 rounded-lg">
                Load a property and edit inputs to see live scenarios.
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
